import React, { useContext, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon, Illustration } from '@node-space/ui-components'
import { Approval } from 'constants/General'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BeneficiaryType } from 'types/beneficiaries'
import { getBeneficiaryName } from 'utils/beneficiaries'
import { roundNumberWithCommas } from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'

export const Complete = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [walletAction] = useContext(WalletActionContext)
  const selectedWallet = useAtomValue(selectedWalletAtom)
  const fiatBeneficiary = useAtomValue(selectedFiatBeneficiaryAtom)
  const form = useAtomValue(fiatSendFormAtom)

  const { formValues } = form || {}

  const isFirstParty = fiatBeneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY

  const amountSummary = {
    amount: formValues?.amount,
    currency: selectedWallet?.currency,
  }
  const approvalType = isFirstParty
    ? Approval.FLOWS.FIAT_1ST_PARTY_PAYOUT
    : Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT

  const { approvalsIsRequiredForFlow, isFetchingApprovalPreference } = useApprovalsConfigForFlow(
    approvalType,
    amountSummary
  )

  const isRequiresApprovalModalVisible = approvalsIsRequiredForFlow && !isFetchingApprovalPreference

  const handleComplete = async () => {
    walletAction?.onClose()
    queryClient.invalidateQueries({
      queryKey: reactQueryKeys.customerWallets({
        customerReference: walletAction?.meta?.customerWallet?.customerReference,
      }),
    })
  }

  const headline = useMemo(() => {
    if (isRequiresApprovalModalVisible) {
      return t('wallets.fiatSend.requiresApprovalHeadline', {
        currencyAmount: `${roundNumberWithCommas(formValues?.amount)} ${selectedWallet?.currency}`,
        beneficiaryName: getBeneficiaryName(fiatBeneficiary),
      })
    }
    return t('wallets.fiatSend.eofHeadline', {
      currencyAmount: `${roundNumberWithCommas(formValues?.amount)} ${selectedWallet?.currency}`,
      beneficiaryName: getBeneficiaryName(fiatBeneficiary),
    })
  }, [isRequiresApprovalModalVisible])

  const description = useMemo(() => {
    if (isRequiresApprovalModalVisible) {
      return t('wallets.fiatSend.requiresApprovalDescription')
    }
    return t('wallets.fiatSend.eofDescription')
  }, [isRequiresApprovalModalVisible])

  if (isFetchingApprovalPreference) {
    return (
      <Box flex justify="center" align="center" className="h-full animate-spin">
        <Icon name="Spinner" />
      </Box>
    )
  }

  return (
    <Box
      flex
      flexCol
      justify="center"
      align="center"
      className="gap-5 text-center"
      data-testId="complete-success"
    >
      {isRequiresApprovalModalVisible ? (
        <Illustration name="HourGlassLow" color="info" />
      ) : (
        <Illustration name="HandsClapping" color="success" />
      )}
      <h1 className="text-foreground-prominent text-h1 max-w-[414px]">{headline}</h1>
      <p className="text-foreground-prominent text-body-regular max-w-[370px]">{description}</p>
      <Button onClick={handleComplete}>{t('wallets.fiatSend.done')}</Button>
    </Box>
  )
}
